import { useState } from 'react'
import { ChevronDown, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'

interface FAQItem {
  question: string
  answer: string
}

interface FAQAccordionProps {
  title?: string
  subtitle?: string
  faqs: FAQItem[]
  showCTA?: boolean
  ctaText?: string
  ctaLink?: string
  onCTAClick?: () => void
}

const FAQAccordion = ({ 
  title = "Sıkça Sorulan Sorular",
  subtitle,
  faqs,
  showCTA = true,
  ctaText = "Toplantı Planlayalım",
  ctaLink = "/contact",
  onCTAClick
}: FAQAccordionProps) => {
  const [openIndexes, setOpenIndexes] = useState<Set<number>>(new Set())

  const toggleFAQ = (index: number) => {
    setOpenIndexes(prev => {
      const newSet = new Set(prev)
      if (newSet.has(index)) {
        newSet.delete(index)
      } else {
        newSet.add(index)
      }
      return newSet
    })
  }

  // Split FAQs into two columns for desktop
  const midPoint = Math.ceil(faqs.length / 2)
  const leftColumn = faqs.slice(0, midPoint)
  const rightColumn = faqs.slice(midPoint)

  return (
    <section className="bg-[#f9f9f9] py-16 md:py-20">
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-xs font-medium text-gray-500 mb-3">SSS</p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          {subtitle && (
            <p className="text-base md:text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
          {showCTA && (
            onCTAClick ? (
              <button
                onClick={onCTAClick}
                className="inline-flex items-center justify-center bg-black text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors duration-200"
              >
                {ctaText}
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            ) : (
              <Link
                to={ctaLink}
                className="inline-flex items-center justify-center bg-black text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors duration-200"
              >
                {ctaText}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            )
          )}
        </div>

        {/* FAQ Grid - 2 columns on desktop, 1 column on mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            {leftColumn.map((faq, index) => {
              const actualIndex = index
              const isOpen = openIndexes.has(actualIndex)
              return (
                <div
                  key={actualIndex}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden transition-all duration-200 hover:border-gray-300"
                >
                  <button
                    onClick={() => toggleFAQ(actualIndex)}
                    className="w-full px-4 py-4 flex items-center justify-between text-left group"
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${actualIndex}`}
                  >
                    <span className="text-sm md:text-base font-semibold text-gray-900 pr-4 group-hover:text-gray-700 transition-colors">
                      {faq.question}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <div
                    id={`faq-answer-${actualIndex}`}
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-4 pb-4">
                      <p className="text-sm text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            {rightColumn.map((faq, index) => {
              const actualIndex = index + midPoint
              const isOpen = openIndexes.has(actualIndex)
              return (
                <div
                  key={actualIndex}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden transition-all duration-200 hover:border-gray-300"
                >
                  <button
                    onClick={() => toggleFAQ(actualIndex)}
                    className="w-full px-4 py-4 flex items-center justify-between text-left group"
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${actualIndex}`}
                  >
                    <span className="text-sm md:text-base font-semibold text-gray-900 pr-4 group-hover:text-gray-700 transition-colors">
                      {faq.question}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <div
                    id={`faq-answer-${actualIndex}`}
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-4 pb-4">
                      <p className="text-sm text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}

export default FAQAccordion













